import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bot, ChevronDown, ChevronUp, ExternalLink, Link2, Star, User } from "lucide-react";
import { Badge } from "./Badge";
import { EntityLink } from "./EntityLink";
import { displayLabel, formatDate, getAdminEntityUrl, getEventWebUrl } from "../../utils/helpers";

// Duplicate-review views show raw stored dates (dd/mm/yyyy), not the localized
// `helpers.formatDate` used elsewhere in the admin.
export const formatDuplicateDate = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

export const formatUrlLabel = (url) => {
  if (!url) return "";

  try {
    const parsed = new URL(url);
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const lastPathPart = pathParts.at(-1);
    return lastPathPart ? `${parsed.hostname}/…/${lastPathPart}` : parsed.hostname;
  } catch {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url;
  }
};

/** Absolute timestamp for a resolved pair, in the viewer's locale. */
export const formatReviewDateTime = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

// The artist and venue queues both list *every* event referencing the entity — past and
// cancelled included, since a shared booking history is the strongest signal that two records
// are the same act/room. This line carries the context that tells those apart: when it ran, and
// why it isn't a plain upcoming show.
function eventMetaLine({ start_date: start, end_date: end, is_past: isPast, is_cancelled: isCancelled, status }) {
  const dates = start && (end && end !== start ? `${formatDate(start)} – ${formatDate(end)}` : formatDate(start));
  return [
    dates,
    isPast && "past",
    isCancelled && "cancelled",
    status && status !== "active" && status,
  ].filter(Boolean).join(" · ");
}

/**
 * One referenced event in a duplicate-review card. The title opens the event's detail modal in
 * the admin; the icon opens the consumer page when the web base URL can be resolved.
 */
export function ReviewEventLink({ event }) {
  const { id: eventId } = event;
  const title = displayLabel(event.preferred_title, event.title);
  const adminUrl = getAdminEntityUrl("event", eventId);
  const webUrl = eventId ? getEventWebUrl(eventId) : null;
  const meta = eventMetaLine(event);

  return (
    <li className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {adminUrl ? (
          <Link
            to={adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={title || eventId}
            className="min-w-0 flex-1 truncate text-primary hover:underline"
          >
            {title || eventId}
          </Link>
        ) : (
          <span className="min-w-0 flex-1 truncate text-foreground">{title || eventId}</span>
        )}
        {webUrl && (
          <a
            href={webUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="View on Applauss"
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {meta && <p className="truncate text-[10px] text-muted-foreground">{meta}</p>}
    </li>
  );
}

/**
 * How and by whom an entity was merged away. `children` holds the per-entity stats that follow
 * the shared "N events repointed" line (users repointed for artists, id conflicts for venues).
 */
export function ResolutionDetails({ resolution, mergeTypeLabels = {}, mergeTypeVariants = {}, children }) {
  const when = formatReviewDateTime(resolution?.resolved_at);
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <MergeResolutionBadge resolution={resolution} />
        <Badge variant={mergeTypeVariants[resolution?.merge_type] || "secondary"} className="text-[10px]">
          {mergeTypeLabels[resolution?.merge_type] || resolution?.merge_type || "Merge"}
        </Badge>
        {when && <span>{when}</span>}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {resolution?.resolved_by_email && (
          <span>
            By <span className="font-medium text-foreground">{resolution.resolved_by_email}</span>
          </span>
        )}
        <span>{resolution?.events_repointed || 0} events repointed</span>
        {children}
      </div>
    </div>
  );
}

/** One titled, rule-separated block inside an event comparison card. */
export function EventCardSection({ icon: Icon, title, children }) {
  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </p>
      {children}
    </div>
  );
}

function UrlRow({ urlRef, providerWidth }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className={`${providerWidth} shrink-0 truncate text-muted-foreground`}>
        {urlRef.provider || "unknown"}
      </span>
      <a
        href={urlRef.url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 flex-1 truncate text-blue-600 hover:underline"
        title={urlRef.url}
      >
        {formatUrlLabel(urlRef.url)}
      </a>
    </div>
  );
}

export function EventOccurrences({ occurrences = [], maxHeight = "max-h-56" }) {
  if (!occurrences.length) {
    return <p className="text-xs text-muted-foreground">No occurrences</p>;
  }

  return (
    <div className={`${maxHeight} overflow-y-auto divide-y divide-border border-y border-border pr-1`}>
      {occurrences.map((occ, i) => (
        <div key={occ.id || `${occ.local_date}-${occ.local_time}-${i}`} className="py-1.5">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-medium text-foreground">
              {occ.local_date ? formatDuplicateDate(occ.local_date) : "Date TBA"}
              {occ.local_time ? ` · ${occ.local_time}` : ""}
            </span>
            {occ.status && (
              <span className="shrink-0 text-[10px] text-muted-foreground">{occ.status}</span>
            )}
          </div>
          {occ.name && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{occ.name}</p>}
          {occ.urls?.length > 0 && (
            <div className="mt-1 space-y-0.5 text-[11px]">
              {occ.urls.map((urlRef, urlIndex) => (
                <UrlRow key={`${urlRef.url}-${urlIndex}`} urlRef={urlRef} providerWidth="w-16" />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function EventUrls({ urls = [] }) {
  if (!urls.length) {
    return <p className="text-xs text-muted-foreground">No event-level URLs</p>;
  }

  return (
    <div className="space-y-1 text-xs">
      {urls.map((urlRef, i) => (
        <UrlRow key={`${urlRef.url}-${i}`} urlRef={urlRef} providerWidth="w-20" />
      ))}
    </div>
  );
}

export function EventArtists({ artists = [] }) {
  if (!artists.length) {
    return <p className="text-xs text-muted-foreground">No artists</p>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {artists.map((artist, i) => (
        <span
          key={artist.id || `${artist.name}-${i}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
            artist.headliner
              ? "border-primary/40 bg-primary/10 font-medium text-foreground"
              : "border-border bg-muted/40 text-muted-foreground"
          }`}
          title={artist.headliner ? `${artist.name} (headliner)` : artist.name}
        >
          <EntityLink type="artist" id={artist.id} newTab>{artist.name || "Unnamed artist"}</EntityLink>
          {artist.headliner && <Star className="h-2.5 w-2.5 shrink-0 text-primary" />}
        </span>
      ))}
    </div>
  );
}

// Hard cap on how much description a comparison card ever renders. Enough to judge
// whether two events describe the same show; the event editor holds the full text.
const DESCRIPTION_CHAR_LIMIT = 600;

function trimDescription(description, charLimit) {
  const full = (description || "").trim();
  if (full.length <= charLimit) return { text: full, trimmed: false };

  // Cut on a word boundary when there is one reasonably close to the limit.
  const lastSpace = full.lastIndexOf(" ", charLimit);
  const end = lastSpace > charLimit * 0.6 ? lastSpace : charLimit;
  return { text: `${full.slice(0, end).trimEnd()}…`, trimmed: true };
}

export function EventDescription({ description, charLimit = DESCRIPTION_CHAR_LIMIT }) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const { text, trimmed } = trimDescription(description, charLimit);

  // Measure the real clamp instead of guessing from length: only while collapsed,
  // since an expanded paragraph never overflows and would hide its own toggle.
  useEffect(() => {
    const el = textRef.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  if (!text) {
    return <p className="text-xs text-muted-foreground">No description</p>;
  }

  return (
    <div>
      <p
        ref={textRef}
        className={`whitespace-pre-line text-xs text-muted-foreground ${expanded ? "" : "line-clamp-4"}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-medium text-blue-600 hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
      {expanded && trimmed && (
        <p className="mt-1 text-[10px] italic text-muted-foreground">
          Shortened for review — open the event to read the full description.
        </p>
      )}
    </div>
  );
}

export function SimilarityBadge({ score, destructiveAt = 85, warningAt = 70 }) {
  const pct = Math.round((score || 0) * 100);
  const variant = pct >= destructiveAt ? "destructive" : pct >= warningAt ? "warning" : "secondary";
  return <Badge variant={variant}>{pct}% match</Badge>;
}

export function ComparisonGrid({ left, right, separator = "vs" }) {
  return (
    <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
      {left}
      <div className="flex shrink-0 items-center justify-center text-xs font-medium text-muted-foreground">
        {separator}
      </div>
      {right}
    </div>
  );
}

function ExpandableDuplicateCard({ icon, title, subtitle, end, children, defaultExpanded, stopEndClick = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
      <div
        className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
        onClick={() => setExpanded((v) => !v)}
      >
        {icon}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          {subtitle}
        </div>
        {end && (
          <div className="shrink-0" onClick={stopEndClick ? (e) => e.stopPropagation() : undefined}>
            {end}
          </div>
        )}
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </div>
      {expanded && <div className="space-y-4 border-t border-border px-4 py-4">{children}</div>}
    </div>
  );
}

/**
 * The merge_occurrences opt-out, shared by every screen that resolves an event
 * duplicate. Checked means `merge_occurrences=false`: the source is only flagged.
 * Images, links and classification are absorbed either way — that's what the API
 * does, so don't promise otherwise in the copy here.
 */
export function OnlyMarkDuplicateToggle({ checked, onChange, disabled = false }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-3 py-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
      />
      <span>
        <span className="block text-sm font-medium text-foreground">Only mark as duplicate</span>
        <span className="block text-xs text-muted-foreground mt-0.5">
          Do not absorb occurrences or ticket links from the duplicate event.
        </span>
      </span>
    </label>
  );
}

export function DuplicateReviewCard({
  title,
  reason,
  score,
  children,
  defaultExpanded = true,
  scoreDestructiveAt,
  scoreWarningAt,
}) {
  return (
    <ExpandableDuplicateCard
      icon={<AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />}
      title={title}
      subtitle={reason && <p className="text-xs text-muted-foreground">{reason}</p>}
      end={<SimilarityBadge score={score} destructiveAt={scoreDestructiveAt} warningAt={scoreWarningAt} />}
      defaultExpanded={defaultExpanded}
    >
      {children}
    </ExpandableDuplicateCard>
  );
}

export function MergedDuplicateCard({ icon, title, meta, action, children, defaultExpanded = false }) {
  return (
    <ExpandableDuplicateCard
      icon={icon}
      title={title}
      subtitle={meta && <div className="mt-0.5 flex items-center gap-2">{meta}</div>}
      end={action}
      defaultExpanded={defaultExpanded}
      stopEndClick
    >
      {children}
    </ExpandableDuplicateCard>
  );
}

export function MergeResolutionBadge({ resolution, adminLabel = "Admin", automaticLabel = "Automatic" }) {
  const isAdmin = resolution?.method === "admin";
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        isAdmin ? "border-blue-200 bg-blue-50 text-blue-700" : "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {isAdmin ? <User className="h-3 w-3 shrink-0" /> : <Bot className="h-3 w-3 shrink-0" />}
      {isAdmin ? adminLabel : automaticLabel}
    </div>
  );
}
